/**
 * OAuth X Complete Page - P0 FIX ÉCRAN BLANC
 * ==========================================
 * Route publique stable pour le retour OAuth X.
 * 
 * Cette page:
 * 1. Reçoit le callback OAuth depuis le backend (302 redirect)
 * 2. Vérifie le statut de connexion X
 * 3. Redirige vers /calendar ou affiche une erreur
 * 
 * NE JAMAIS rediriger vers une route /api/* côté front.
 * Route: /oauth/x/complete
 * 
 * P1 DEPLOY: This page must NEVER be cached (Safari iOS)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';

// Error messages mapping
const ERROR_MESSAGES = {
  'OAUTH1_CALLBACK_FAILED': 'Échec de l\'autorisation OAuth X. Veuillez réessayer.',
  'INVALID_VERIFIER': 'Session OAuth expirée. Veuillez relancer le processus.',
  'ACCESS_TOKEN_FAILED': 'Impossible d\'obtenir les tokens d\'accès X.',
  'MISSING_CREDENTIALS': 'Credentials API Key/Secret manquants. Configurez-les d\'abord.',
  'CALLBACK_NOT_APPROVED': 'L\'URL de callback n\'est pas approuvée dans le portail X Developer.',
  'STATE_EXPIRED': 'La session d\'autorisation a expiré. Réessayez.',
  'UNKNOWN': 'Une erreur inattendue s\'est produite.'
};

/**
 * OAuth X Complete Page Component
 */
export default function OAuthXComplete() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // State
  const [status, setStatus] = useState('loading'); // loading | verifying | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [screenName, setScreenName] = useState('');
  const [projectId, setProjectId] = useState('');
  
  // Extract query params
  const success = searchParams.get('success');
  const error = searchParams.get('error');
  const paramProjectId = searchParams.get('project_id');
  const paramScreenName = searchParams.get('screen_name');
  
  // Verify connection status
  const verifyConnection = useCallback(async (pid) => {
    try {
      setStatus('verifying');
      
      // Call status endpoint with verify=true
      const response = await apiClient.get(`/integrations/x/status?project_id=${pid}&verify=true`);
      const data = response.data;
      
      console.log('[OAuthXComplete] Status response:', {
        connected: data.connected,
        configured: data.configured,
        username: data.username || data.screen_name
      });
      
      if (data.connected === true) {
        setScreenName(data.username || data.screen_name || paramScreenName || '');
        setStatus('success');
        
        // Dispatch global event to invalidate caches
        window.dispatchEvent(new CustomEvent('ame:x-connected', {
          detail: { 
            screenName: data.username || data.screen_name,
            projectId: pid,
            timestamp: Date.now()
          }
        }));
        
        // Redirect to calendar after brief success display
        setTimeout(() => {
          navigate(`/projects/${pid}/setup/connect`, { replace: true });
        }, 1500);
        
      } else {
        // Status says not connected - something went wrong
        setStatus('error');
        setErrorMessage(data.reason || 'Connexion non établie. Veuillez réessayer.');
      }
      
    } catch (err) {
      console.error('[OAuthXComplete] Verify error:', err);
      setStatus('error');
      setErrorMessage(err.response?.data?.message || 'Erreur lors de la vérification.');
    }
  }, [navigate, paramScreenName]);
  
  // Main effect - handle OAuth callback
  useEffect(() => {
    try {
      // Store project ID
      if (paramProjectId) {
        setProjectId(paramProjectId);
      }
      
      // P0 FIX: Handle legacy callback params (x_oauth1=success/error) in addition to /oauth/x/complete
      // This ensures backwards compatibility during transition
      const legacyOauthResult = searchParams.get('x_oauth1');
      const legacyScreenName = searchParams.get('screen_name');
      const legacyErrorCode = searchParams.get('code');
      
      if (legacyOauthResult === 'success') {
        // Legacy success callback - trigger verification
        toast.success(`Connecté à X en tant que @${legacyScreenName || 'utilisateur'}`);
        
        // Clean legacy URL params
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('x_oauth1');
        newParams.delete('screen_name');
        newParams.delete('code');
        newParams.delete('ts');
        window.history.replaceState({}, '', window.location.pathname + (newParams.toString() ? '?' + newParams.toString() : ''));
        
        // Verify and notify
        if (paramProjectId) {
          verifyConnection(paramProjectId);
        }
        return;
        
      } else if (legacyOauthResult === 'error') {
        // Legacy error callback
        setStatus('error');
        setErrorMessage(ERROR_MESSAGES[legacyErrorCode] || ERROR_MESSAGES['UNKNOWN']);
        
        // Clean URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('x_oauth1');
        newParams.delete('code');
        newParams.delete('ts');
        window.history.replaceState({}, '', window.location.pathname + (newParams.toString() ? '?' + newParams.toString() : ''));
        return;
      }
      
      // Handle success=0 (error from backend via /oauth/x/complete)
      if (success === '0') {
        setStatus('error');
        const errorCode = error || 'UNKNOWN';
        setErrorMessage(ERROR_MESSAGES[errorCode] || ERROR_MESSAGES['UNKNOWN']);
        return;
      }
      
      // Handle success=1 (success from backend via /oauth/x/complete)
      if (success === '1' && paramProjectId) {
        verifyConnection(paramProjectId);
        return;
      }
      
      // No valid params - show error
      if (!success && !paramProjectId) {
        setStatus('error');
        setErrorMessage('Paramètres de callback manquants. Retournez à la configuration.');
        return;
      }
      
      // Fallback: try to verify if we have project_id
      if (paramProjectId) {
        verifyConnection(paramProjectId);
      } else {
        setStatus('error');
        setErrorMessage('ID de projet manquant. Retournez au tableau de bord.');
      }
      
    } catch (err) {
      // Global catch to prevent white screen
      console.error('[OAuthXComplete] Unexpected error:', err);
      setStatus('error');
      setErrorMessage('Une erreur inattendue s\'est produite.');
    }
  }, [success, error, paramProjectId, searchParams, verifyConnection]);
  
  // Navigate back handler
  const handleBack = () => {
    if (projectId) {
      navigate(`/projects/${projectId}/setup/connect`, { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  };
  
  // Retry handler
  const handleRetry = () => {
    if (projectId) {
      navigate(`/projects/${projectId}/setup/connect`, { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-zinc-900 rounded-2xl border border-zinc-800 p-8 text-center">
          
          {/* X Logo */}
          <div className="w-16 h-16 mx-auto mb-6 bg-black rounded-2xl flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-white fill-current">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </div>
          
          {/* Loading State */}
          {(status === 'loading' || status === 'verifying') && (
            <>
              <Loader2 className="w-8 h-8 mx-auto mb-4 text-blue-500 animate-spin" />
              <h1 className="text-xl font-semibold text-white mb-2">
                {status === 'loading' ? 'Connexion X en cours...' : 'Vérification...'}
              </h1>
              <p className="text-zinc-400 text-sm">
                Veuillez patienter pendant que nous finalisons la connexion.
              </p>
            </>
          )}
          
          {/* Success State */}
          {status === 'success' && (
            <>
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
              <h1 className="text-xl font-semibold text-white mb-2">
                Connexion réussie !
              </h1>
              {screenName && (
                <p className="text-emerald-400 font-medium mb-2">@{screenName}</p>
              )}
              <p className="text-zinc-400 text-sm">
                Redirection en cours...
              </p>
            </>
          )}
          
          {/* Error State */}
          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
              <h1 className="text-xl font-semibold text-white mb-2">
                Échec de la connexion
              </h1>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300 text-left">
                    {errorMessage}
                  </p>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleRetry}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-xl py-3 font-medium transition-colors"
                >
                  Réessayer
                </button>
                <button
                  onClick={handleBack}
                  className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl py-3 font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour
                </button>
              </div>
            </>
          )}
        </div>
        
        {/* Footer */}
        <p className="text-xs text-zinc-600 text-center mt-4">
          OAuth 1.0a · AI Marketing Engine
        </p>
      </div>
    </div>
  );
}
